import React, { useEffect, useRef, useMemo, useState } from "react";
import * as echarts from "echarts";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import generateToastContainer from "../utils/ToastContainer";

import "./CorporateForm.css";
import Navbar from "../components/Navbar/Navbar";
import { apiCall } from "../api/apiCall";
import { CurrentLeaderboardUser, MonthlyLeaderboardRecord } from "../models/my_gamification";
import TopHeader from "../components/Navbar/TopHeader";
import FeatureGate from "../components/FeatureGate";
import { useTranslation } from "../i18n/UseTranslation";
import { getAuth, onAuthStateChanged } from "firebase/auth";

function CorporateForm() {
  const { t, language } = useTranslation();
  const chartRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient(); 

  const currentDate = new Date();
  const [selectedMonth, setSelectedMonth] = useState<number>(currentDate.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState<number>(currentDate.getFullYear());

  const [isDarkMode, setIsDarkMode] = useState(
    document.documentElement.getAttribute("data-theme") === "dark"
  );

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDarkMode(document.documentElement.getAttribute("data-theme") === "dark");
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    return () => observer.disconnect();
  }, []);

  const [isAuthReady, setIsAuthReady] = useState(false);

  useEffect(() => {
        const auth = getAuth(); 
        const unsubscribe = onAuthStateChanged(auth, (user) => {
          setIsAuthReady(true);
        });
        return () => unsubscribe();
      }, []);

  const { data: me, isLoading: isLoadingMe, isError: isErrorMe } = useQuery({
    queryKey: ["me"],
    queryFn: () => apiCall<any>("/users/me"),
    enabled: isAuthReady,
  });

  const userToGlimpse = me as any;

  const userSubscription = 
    userToGlimpse?.Company?.Subscription || 
    userToGlimpse?.company?.Subscription ||
    userToGlimpse?.company?.subscription;

  const currentPlan = userSubscription?.status === "active" 
    ? userSubscription.plan_code 
    : "free";

  const companyUuid = me?.company_uuid;

  const { data: liveLeaderboard, isLoading: isLoadingLive, isError: isErrorLive } = useQuery<CurrentLeaderboardUser[], Error>({
    queryKey: ["liveLeaderboard", companyUuid],
    queryFn: () => apiCall<CurrentLeaderboardUser[]>(`/admin/gamification/leaderboard/${companyUuid}`),
    enabled: !!companyUuid && isAuthReady,
  });

  const { data: monthlyHistory, isLoading: isLoadingHistory, isError: isErrorHistory } = useQuery<MonthlyLeaderboardRecord[], Error>({
    queryKey: ["monthlyLeaderboard", companyUuid, selectedMonth, selectedYear],
    queryFn: () =>
      apiCall<MonthlyLeaderboardRecord[]>(
        `/admin/gamification/leaderboard/${companyUuid}/monthly?month=${selectedMonth}&year=${selectedYear}`
      ),
    enabled: !!companyUuid && isAuthReady,
  });

  const snapshotMutation = useMutation({
    mutationFn: () =>
      apiCall(`/admin/gamification/leaderboard/${companyUuid}/snapshot`, {
        method: "POST",
        body: JSON.stringify({ month: selectedMonth, year: selectedYear }),
      }),
    onSuccess: () => {
      toast.success(`${t.corporate.snapshotSuccess} ${selectedMonth}/${selectedYear} !`);
      queryClient.invalidateQueries({ queryKey: ["monthlyLeaderboard", companyUuid, selectedMonth, selectedYear] });
    },
    onError: (error: Error) => {
      toast.error(`${t.corporate.snapshotError} ${error.message}`);
    },
  });

  const handleTakeSnapshot = () => {
    const confirmMessage = t.corporate.snapshotConfirm
      .replace("{month}", String(selectedMonth))
      .replace("{year}", String(selectedYear));
      
    if (window.confirm(confirmMessage)) {
      snapshotMutation.mutate();
    }
  };

  const isLoading = isLoadingMe || isLoadingLive || isLoadingHistory;
  const isError = isErrorMe || isErrorLive || isErrorHistory;

  const stats = useMemo(() => {
    if (!liveLeaderboard) return { totalParticipants: 0, topStreak: 0, totalFlames: 0 };
    
    const participantsWithFlames = liveLeaderboard.filter((u) => u.streak > 0).length;
    const maxStreak = liveLeaderboard.length > 0 ? liveLeaderboard[0].streak : 0;
    const allFlames = liveLeaderboard.reduce((acc, curr) => acc + curr.streak, 0);

    return {
      totalParticipants: participantsWithFlames,
      topStreak: maxStreak,
      totalFlames: allFlames,
    };
  }, [liveLeaderboard]);

  useEffect(() => {
    if (!monthlyHistory || monthlyHistory.length === 0 || !chartRef.current) return;

    const theme = isDarkMode ? "dark" : undefined;
    const chart = echarts.init(chartRef.current, theme);
    const top10 = [...monthlyHistory].sort((a, b) => a.rank - b.rank).slice(0, 10);
    const names = top10.map((r) => `${r.user.first_name} ${r.user.last_name.charAt(0)}.`);
    const scores = top10.map((r) => r.flames_at_time);

    chart.setOption({
      backgroundColor: "transparent",
      title: { text: t.corporate.chartTitle, textStyle: { fontSize: 14, color: isDarkMode ? "#94a3b8" : "#64748b" } },
      tooltip: { trigger: "axis", formatter: "{b} : {c} 🔥" },
      grid: { left: "5%", right: "5%", bottom: "10%", containLabel: true },
      xAxis: { type: "category", data: names, axisLabel: { interval: 0, fontSize: 11 } },
      yAxis: { type: "value", minInterval: 1 },
      series: [{
        data: scores,
        type: "bar",
        itemStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: "#f59e0b" }, 
            { offset: 1, color: "#fbbf24" }
          ]),
          borderRadius: [6, 6, 0, 0],
        },
        label: { show: true, position: 'top', formatter: '{c} 🔥', color: isDarkMode ? "#f1f5f9" : "#1e293b" }
      }],
    });

    const handleResize = () => chart.resize();
    window.addEventListener("resize", handleResize);

    return () => {
      chart.dispose();
      window.removeEventListener("resize", handleResize);
    };
  }, [monthlyHistory, isDarkMode, t]);

  if (isLoading) {
    return (
      <div style={{ paddingTop: "70px" }}>
        <Navbar />
        <TopHeader />
        <div className="dashboard-wrapper dashboard-state-message">
          <FeatureGate userPlan={currentPlan} requiredPlan="essential" featureName="la gestion des classements et statistiques">
            <p className="loading-text">{t.corporate.loading}</p>
          </FeatureGate>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div style={{ paddingTop: "70px" }}>
        <Navbar />
        <TopHeader />
        <div className="dashboard-wrapper dashboard-state-message">
          <FeatureGate userPlan={currentPlan} requiredPlan="essential" featureName="la gestion des classements et statistiques">
            <p className="error-text">{t.corporate.errorLoading}</p>
          </FeatureGate>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-wrapper">      
      <Navbar />
      <TopHeader />

      <div className="dashboard-content">      
        <div className="corporate-main-container">
          
          <div className="corporate-header">
            <div>
              <h1>{t.corporate.title}</h1>
              <p>{t.corporate.subtitle}</p>
            </div>
          </div>
          
          <FeatureGate userPlan={currentPlan} requiredPlan="essential" featureName="la gestion des classements et statistiques">

            
            <section className="stats-grid">
              <StatCard title={t.corporate.statActive} value={stats.totalParticipants} variant="primary" />
              <StatCard title={t.corporate.statTopStreak} value={stats.topStreak} variant="warning" />
              <StatCard title={t.corporate.statTotalFlames} value={stats.totalFlames} variant="success" />
            </section>

            <div className="corporate-layout-grid">
              
              <div className="leaderboard-live-card">
                <div className="card-header">
                  <h2>{t.corporate.liveLeaderboard}</h2>
                  <span className="live-badge">{t.corporate.liveBadge}</span>
                </div>
                
                <div className="leaderboard-list">
                  {liveLeaderboard?.map((user, index) => {
                    const rank = index + 1;
                    let rankClass = "rank-standard";
                    if (rank === 1) rankClass = "rank-gold";
                    else if (rank === 2) rankClass = "rank-silver";
                    else if (rank === 3) rankClass = "rank-bronze";

                    return (
                      <div key={user.firebaseUid} className={`leaderboard-item ${rankClass}`}>
                        <div className="rank-number">#{rank}</div>
                        <div className="user-info">
                          <div className="avatar">
                            {user.profile_picture ? (
                              <img src={user.profile_picture} alt="Avatar" />
                            ) : (
                              <span>{user.first_name[0]}{user.last_name[0]}</span>
                            )}
                          </div>
                          <span className="user-name">{user.first_name} {user.last_name}</span>
                        </div>
                        <div className="streak-score">
                          <span className="streak-number">{user.streak}</span>
                          <span className="fire-icon">🔥</span>
                        </div>
                      </div>
                    );
                  })}
                  {(!liveLeaderboard || liveLeaderboard.length === 0) && (
                    <p className="loading-text">{t.corporate.noActiveParticipants}</p>
                  )}
                </div>
              </div>

              <div className="corporate-right-col">
                <div className="history-card">
                  <div className="card-header">
                    <h2>{t.corporate.historicalPodium}</h2>
                    
                    <div className="header-actions-right">
                      <div className="date-selectors">
                        <select value={selectedMonth} onChange={(e) => setSelectedMonth(Number(e.target.value))}>
                          {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => {
                            // On crée une date fictive pour avoir le nom du mois dans la bonne langue
                            const date = new Date(2000, m - 1);
                            // Détection basique pour le locale de formatage (fr-FR, en-US, es-ES)
                            const locale = language === 'fr' ? 'fr-FR' : (language === 'es' ? 'es-ES' : 'en-US');
                            let monthName = date.toLocaleString(locale, { month: 'long' });
                            monthName = monthName.charAt(0).toUpperCase() + monthName.slice(1);
                            return (
                              <option key={m} value={m}>
                                {monthName}
                              </option>
                            );
                          })}
                        </select>
                        <select value={selectedYear} onChange={(e) => setSelectedYear(Number(e.target.value))}>
                          {[currentDate.getFullYear() - 1, currentDate.getFullYear(), currentDate.getFullYear() + 1].map((y) => (
                            <option key={y} value={y}>{y}</option>
                          ))}
                        </select>
                      </div>

                      <button 
                        className="snapshot-btn"
                        onClick={handleTakeSnapshot}
                        disabled={snapshotMutation.isPending || !companyUuid}
                      >
                        {snapshotMutation.isPending ? t.corporate.btnSaving : t.corporate.btnSnapshot}
                      </button>
                    </div>
                  </div>

                  {monthlyHistory && monthlyHistory.length > 0 ? (
                    <div ref={chartRef} className="echarts-container" />
                  ) : (
                    <p className="loading-text">{t.corporate.noDataRecorded}</p>
                  )}
                </div>

                <div className="info-card">
                  <h3>{t.corporate.howItWorksTitle}</h3>
                  <ul style={{ paddingLeft: "20px", color: "inherit", fontSize: "0.95rem", lineHeight: "1.6", margin: "10px 0 0 0" }}>
                    <li>{t.corporate.howItWorksL1}</li>
                    <li>{t.corporate.howItWorksL2}</li>
                    <li>{t.corporate.howItWorksL3}</li>
                    <li>{t.corporate.howItWorksL4}</li>
                  </ul>
                </div>
              </div>
            </div>
          </FeatureGate>
        </div>
      </div>
      {generateToastContainer()}
    </div>
  );
}

function StatCard({ title, value, variant }: { title: string; value: number; variant: string }) {
  return (
    <div className={`stat-card stat-card--${variant}`}>
      <span className="stat-card__title">{title}</span>
      <span className="stat-card__value">{value}</span>
    </div>
  );
}

export default CorporateForm;